import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getStoreSettings,
  getStoreApiPayload,
  isStoreApiSuccess,
  patchStoreSettings,
  syncHubbleBrands,
} from '../../services/storeApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';

function StoreSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fePerInr, setFePerInr] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStoreSettings();
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load settings', 'error');
        return;
      }
      const payload = getStoreApiPayload(res);
      setFePerInr(payload?.fan_energy_per_inr != null ? String(payload.fan_energy_per_inr) : '');
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    const rate = Number(fePerInr);
    if (!Number.isFinite(rate) || rate <= 0) {
      showSnackbar('Enter a valid positive number for Fan Energy per INR', 'warning');
      return;
    }

    setSaving(true);
    try {
      const res = await patchStoreSettings({ fan_energy_per_inr: rate });
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to save settings', 'error');
        return;
      }
      showSnackbar('Settings saved. Re-sync Hubble brands to update gift card FE prices.');
      loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncHubbleBrands();
      if (handleSessionExpired(res)) return;
      if (!isStoreApiSuccess(res)) {
        showSnackbar(res?.message || 'Sync failed', 'error');
        return;
      }
      const synced = getStoreApiPayload(res)?.synced;
      showSnackbar(synced != null ? `Synced ${synced} brand(s)` : 'Hubble brands synced');
    } finally {
      setSyncing(false);
    }
  };

  const previewFe = Number(fePerInr);
  const feFor100 = Number.isFinite(previewFe) ? Math.ceil(100 * previewFe) : null;

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Store Settings</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <Box sx={{ mb: 2 }}>
                      <Button component={Link} to="/admin/store" startIcon={<ArrowBackIcon />} variant="outlined" size="small">
                        Store dashboard
                      </Button>
                    </Box>

                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Box sx={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="warning">
                          After changing the Fan Energy rate, run <strong>Sync Hubble brands</strong> (or PATCH individual
                          products) so gift card SKUs pick up new FE prices.
                        </Alert>

                        <TextField
                          label="Fan Energy per ₹1 (INR)"
                          helperText="Example: 1.2 means ₹100 costs 120 Fan Energy"
                          type="number"
                          inputProps={{ step: '0.01', min: '0' }}
                          value={fePerInr}
                          onChange={(e) => setFePerInr(e.target.value)}
                          fullWidth
                        />

                        {feFor100 != null && (
                          <Typography variant="body2" color="text.secondary">
                            Preview: ₹100 ≈ {feFor100} Fan Energy
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button variant="contained" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save settings'}
                          </Button>
                          <Button variant="outlined" onClick={handleSync} disabled={syncing}>
                            {syncing ? 'Syncing…' : 'Sync Hubble brands'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default StoreSettings;
